import type { RealtimeChannel } from '@supabase/realtime-js'
import { RealtimeClient } from '@supabase/realtime-js'
import { getRealtimeBaseUrl } from '../utils/realtime'
import { getLoggedinUser } from '../helpers/api_helper'
import { getRealtimeAccessToken, subscribeToTokenUpdates } from './realtimeAuth'
import realtimeLogger from './realtimeLogger'

type LicenseEvent =
  | 'license:assigned'
  | 'license:revoked'
  | 'license:expiring'
  | 'license:renewed'
  | 'license:capacity-warning'
  | 'license:payment-failed'
  | 'license:update'

type LicensePayload = {
  event: LicenseEvent
  business_license_id: number
  business_id: number
  user_id?: number
  status?: string
  data: {
    assignment_id?: number
    status?: string
    assigned_at?: string
    expires_at?: string
    days_until_expiry?: number
    reason?: string
    revoked_at?: string
    auto_renew?: boolean
    capacity_info?: {
      current: number
      max: number
      type: 'users' | 'sites'
    }
    failure_reason?: string
    retry_date?: string
    force_logout?: boolean
  }
}

export type LicenseListener = (payload: LicensePayload) => void

class LicenseManager {
  private client: RealtimeClient | null = null
  private readonly channels = new Map<string, RealtimeChannel>()
  private readonly listeners = new Map<string, Set<LicenseListener>>()
  private tokenUnsubscribe: (() => void) | null = null

  constructor() {
    // Subscribe to token updates
    this.tokenUnsubscribe = subscribeToTokenUpdates((newToken) => {
      this.handleTokenUpdate(newToken)
    })
  }

  /**
   * Handle token update by updating the client's access token
   */
  private handleTokenUpdate(newToken: string): void {
    if (!this.client) {
      console.log('[realtimeLicenses] No client to update with new token')
      return
    }

    console.log('[realtimeLicenses] Updating client with new token')
    
    try {
      // Update the access token in the client
      this.client.setAuth(newToken)
      console.log('[realtimeLicenses] Client token updated successfully')
    } catch (error) {
      console.error('[realtimeLicenses] Failed to update client token:', error)
    }
  }

  private getAnonKey(): string | null {
    return (
      import.meta.env.VITE_SUPABASE_REALTIME_ANON_KEY ??
      import.meta.env.VITE_SUPABASE_ANON_KEY ??
      null
    )
  }

  private async ensureClient(): Promise<RealtimeClient | null> {
    // Check if user is authenticated before creating client
    const auth = getLoggedinUser()
    if (!auth || !auth.token) {
      console.log('[realtimeLicenses] User not authenticated, skipping client creation')
      return null
    }

    if (!this.client) {
      // Get user's JWT token for authentication
      const userToken = await getRealtimeAccessToken()
      if (!userToken) {
        console.error('[realtimeLicenses] Failed to get realtime access token')
        return null
      }

      console.log('[realtimeLicenses] Creating Realtime client with user JWT token')
      this.client = new RealtimeClient(getRealtimeBaseUrl(), {
        params: {
          apikey: userToken,  // Use user's JWT token for authentication
          vsn: '1.0.0',
        },
        accessToken: getRealtimeAccessToken,  // Provide callback for token refresh
      })
      this.client.connect()
    }

    return this.client
  }

  /**
   * Subscribe to all license events for a specific user
   * @param userId - The user ID
   * @param listener - Callback function to handle license events
   * @returns Unsubscribe function
   */
  subscribeToUserLicenses(userId: number | string, listener: LicenseListener): () => void {
    const channelName = `user:${userId}`
    const existing = this.listeners.get(channelName)

    if (existing) {
      existing.add(listener)
    } else {
      this.listeners.set(channelName, new Set([listener]))
      // Create channel asynchronously
      this.createUserChannel(userId).catch((error) => {
        console.error('[realtimeLicenses] Failed to create user channel:', error)
      })
      realtimeLogger.logSubscription(channelName, 'user-licenses')
    }

    return () => {
      this.detach(channelName, listener)
    }
  }

  /**
   * Subscribe to all license events for a specific business
   * @param businessId - The business ID
   * @param listener - Callback function to handle license events
   * @returns Unsubscribe function
   */
  subscribeToBusinessLicenses(businessId: number | string, listener: LicenseListener): () => void {
    const channelName = `business:${businessId}`
    const existing = this.listeners.get(channelName)

    if (existing) {
      existing.add(listener)
    } else {
      this.listeners.set(channelName, new Set([listener]))
      // Create channel asynchronously
      this.createBusinessChannel(businessId).catch((error) => {
        console.error('[realtimeLicenses] Failed to create business channel:', error)
      })
      realtimeLogger.logSubscription(channelName, 'business-licenses')
    }

    return () => {
      this.detach(channelName, listener)
    }
  }

  private async createUserChannel(userId: number | string): Promise<RealtimeChannel | null> {
    const client = await this.ensureClient()
    if (!client) {
      return null
    }

    const channelName = `user:${userId}`
    const existing = this.channels.get(channelName)
    if (existing) {
      return existing
    }

    const channel = client.channel(channelName, {
      config: {
        broadcast: {
          ack: false,
          self: false,
        },
      },
    })

    // Listen for license update events
    channel.on('broadcast', { event: 'license:update' }, ({ payload }) => {
      this.emit(channelName, payload as LicensePayload)
    })

    // Listen for license revocation events
    // Note: The realtime engine only delivers notifications. 
    // Application logic should handle any actions (like logout) based on the notification.
    channel.on('broadcast', { event: 'license:revoked' }, ({ payload }) => {
      const licensePayload = payload as LicensePayload
      
      // Log the revocation event
      console.warn('[realtimeLicenses] License revoked notification received:', licensePayload)
      realtimeLogger.logEventReceived(
        channelName,
        'license:revoked',
        { critical: true, force_logout: licensePayload.data.force_logout }
      )
      
      // Emit to listeners - let the application decide what to do
      this.emit(channelName, licensePayload)
    })

    // Listen for license expiring warnings
    channel.on('broadcast', { event: 'license:expiring' }, ({ payload }) => {
      this.emit(channelName, payload as LicensePayload)
    })

    // Listen for license capacity warnings
    channel.on('broadcast', { event: 'license:capacity-warning' }, ({ payload }) => {
      this.emit(channelName, payload as LicensePayload)
    })

    // Listen for license payment failed events
    channel.on('broadcast', { event: 'license:payment-failed' }, ({ payload }) => {
      this.emit(channelName, payload as LicensePayload)
    })

    channel.subscribe((status) => {
      if (status === 'CHANNEL_ERROR') {
        console.warn(`[realtimeLicenses] Channel error on ${channelName}`)
      }
      if (status === 'SUBSCRIBED') {
        console.log(`[realtimeLicenses] Subscribed to ${channelName}`)
      }
      if (status === 'CLOSED') {
        this.channels.delete(channelName)
      }
    })

    this.channels.set(channelName, channel)
    return channel
  }

  private async createBusinessChannel(businessId: number | string): Promise<RealtimeChannel | null> {
    const client = await this.ensureClient()
    if (!client) {
      return null
    }

    const channelName = `business:${businessId}`
    const existing = this.channels.get(channelName)
    if (existing) {
      return existing
    }

    const channel = client.channel(channelName, {
      config: {
        broadcast: {
          ack: false,
          self: false,
        },
      },
    })

    // Listen for license assigned events
    channel.on('broadcast', { event: 'license:assigned' }, ({ payload }) => {
      this.emit(channelName, payload as LicensePayload)
    })

    // Listen for license revoked events
    channel.on('broadcast', { event: 'license:revoked' }, ({ payload }) => {
      this.emit(channelName, payload as LicensePayload)
    })

    channel.subscribe((status) => {
      if (status === 'CHANNEL_ERROR') {
        console.warn(`[realtimeLicenses] Channel error on ${channelName}`)
      }
      if (status === 'SUBSCRIBED') {
        console.log(`[realtimeLicenses] Subscribed to ${channelName}`)
      }
      if (status === 'CLOSED') {
        this.channels.delete(channelName)
      }
    })

    this.channels.set(channelName, channel)
    return channel
  }

  private emit(channelName: string, payload: LicensePayload): void {
    const listeners = this.listeners.get(channelName)
    if (!listeners || listeners.size === 0) return

    Array.from(listeners).forEach((listener) => {
      try {
        listener(payload)
      } catch (error) {
        console.error('[realtimeLicenses] Listener error:', error)
        realtimeLogger.logEventError(channelName, payload.event, error as Error)
      }
    })
  }

  private detach(channelName: string, listener?: LicenseListener): void {
    const listeners = this.listeners.get(channelName)
    if (!listeners) return

    if (listener) {
      listeners.delete(listener)
    }

    // Clean up channel if no more listeners
    if (listeners.size === 0) {
      this.listeners.delete(channelName)
      const channel = this.channels.get(channelName)
      if (channel) {
        channel.unsubscribe()
        this.channels.delete(channelName)
        console.log(`[realtimeLicenses] Unsubscribed from ${channelName}`)
        realtimeLogger.logUnsubscription(channelName, 'No more listeners')
      }
    }
  }

  /**
   * Disconnect all channels and clean up
   */
  disconnectAll(): void {
    this.channels.forEach((channel, channelName) => {
      channel.unsubscribe()
      console.log(`[realtimeLicenses] Disconnected from ${channelName}`)
    })
    this.channels.clear()
    this.listeners.clear()
    this.client?.disconnect()
    this.client = null

    // Unsubscribe from token updates
    if (this.tokenUnsubscribe) {
      this.tokenUnsubscribe()
      this.tokenUnsubscribe = null
    }
  }
}

// Export singleton instance
const licenseManager = new LicenseManager()

// Export convenience functions
export const subscribeToUserLicenses = (
  userId: number | string,
  listener: LicenseListener
): (() => void) => {
  return licenseManager.subscribeToUserLicenses(userId, listener)
}

export const subscribeToBusinessLicenses = (
  businessId: number | string,
  listener: LicenseListener
): (() => void) => {
  return licenseManager.subscribeToBusinessLicenses(businessId, listener)
}

export default licenseManager
